import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { getRevenueMetricsGetter } from 'src/app/components/echarts-dashboard-item/compose-data-helpers';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import {
    DashboardService,
    IDashboardInitOptions,
} from 'src/app/services/dashboard/dashboard.service';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { defaultColorGetterBuilder } from 'src/app/utils/echarts/series-color';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { Activity30DaysMonthlyDataLoaderFactory } from './data-loaders/acitvity-30days-monthly';
import { AdsDailyLast30DaysDataLoaderFactory } from './data-loaders/ads-daily-last-30days';
import { AdsMonthlyLast16MonthsDataLoaderFactory } from './data-loaders/ads-monthly-last-16months';
import { CampaignAttributionKpisDailyDataLoaderFactory } from './data-loaders/campaign-attribution-kpis-daily';
import { CampaignAttributionKpisMonthlyDataLoaderFactory } from './data-loaders/campaign-attribution-kpis-monthly';
import { CohortRevenueDailyAggDataLoaderFactory } from './data-loaders/cohort-revenue-daily-agg';
import { FirstMarginDailyLast30DaysDataLoaderFactory } from './data-loaders/first-margin-daily-last-30days';
import { FirstMarginMonthlyDataLoaderFactory } from './data-loaders/first-margin-monthly';
import { PlayerKpisDailyDataLoaderFactory } from './data-loaders/player-kpis-daily';
import { PlayerKpisMonthlyDataLoaderFactory } from './data-loaders/player-kpis-monthly';
import { RoasDailyD30DataLoaderFactory } from './data-loaders/roas-daily-d30';
import { RoasDailyD7DataLoaderFactory } from './data-loaders/roas-daily-d7';
import { RoasMonthlyD30DataLoaderFactory } from './data-loaders/roas-monthly-d30';
import { RoasMonthlyD7DataLoaderFactory } from './data-loaders/roas-monthly-d7';
import { UsersDaysDailyD30DataLoaderFactory } from './data-loaders/users-days-daily-d30';
import { UsersDaysDailyD7DataLoaderFactory } from './data-loaders/users-days-daily-d7';
import { UsersDaysMonthlyD30DataLoaderFactory } from './data-loaders/users-days-monthly-d30';
import { BiVizDashboardMasterPlayerKpisDailySqlMap } from './sql-maps/biviz-dashboard-master-users-kpis-daily-sql-map';
import { getGcpProjectIdForEnv } from 'src/app/services/data-environment/models/data-environment.model';

@Component({
    selector: 'app-lastarchon-master',
    templateUrl: './lastarchon-master.component.html',
    styleUrls: ['./lastarchon-master.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class LastArchonMasterComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Master';
    override DASHBOARD_ID = 'lastarchon-master';

    override dashboardInitOptions: IDashboardInitOptions = {
        fullscreenClassName: 'lastarchon-main-content',
    };

    values = Object.values;

    private gcpProjectId: GcpProjectId = 'am-lastarchon-dev';

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why they are created here and referenced later
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'lastarchon.master.activity_30days_monthly':
            new Activity30DaysMonthlyDataLoaderFactory({
                createdInstanceId: 'lastarchon.master.activity_30days_monthly',
                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.ads_daily_last_30days':
            new AdsDailyLast30DaysDataLoaderFactory({
                createdInstanceId: 'lastarchon.master.ads_daily_last_30days',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.ads_monthly_last_16months':
            new AdsMonthlyLast16MonthsDataLoaderFactory({
                createdInstanceId:
                    'lastarchon.master.ads_monthly_last_16months',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.campaign_attribution_kpis_daily':
            new CampaignAttributionKpisDailyDataLoaderFactory({
                createdInstanceId:
                    'lastarchon.master.campaign_attribution_kpis_daily',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.campaign_attribution_kpis_monthly':
            new CampaignAttributionKpisMonthlyDataLoaderFactory({
                createdInstanceId:
                    'lastarchon.master.campaign_attribution_kpis_monthly',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.cohort_revenue_daily_agg':
            new CohortRevenueDailyAggDataLoaderFactory({
                createdInstanceId: 'lastarchon.master.cohort_revenue_daily_agg',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.first_margin_daily_last_30days':
            new FirstMarginDailyLast30DaysDataLoaderFactory({
                createdInstanceId:
                    'lastarchon.master.first_margin_daily_last_30days',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.first_margin_monthly':
            new FirstMarginMonthlyDataLoaderFactory({
                createdInstanceId: 'lastarchon.master.first_margin_monthly',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.player_kpis_daily':
            new PlayerKpisDailyDataLoaderFactory({
                createdInstanceId: 'lastarchon.master.player_kpis_daily',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.player_kpis_monthly':
            new PlayerKpisMonthlyDataLoaderFactory({
                createdInstanceId: 'lastarchon.master.player_kpis_monthly',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.roas_daily_d7': new RoasDailyD7DataLoaderFactory({
            createdInstanceId: 'lastarchon.master.roas_daily_d7',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
        }).create(),

        'lastarchon.master.roas_daily_d30': new RoasDailyD30DataLoaderFactory({
            createdInstanceId: 'lastarchon.master.roas_daily_d30',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
        }).create(),

        'lastarchon.master.roas_monthly_d7': new RoasMonthlyD7DataLoaderFactory(
            {
                createdInstanceId: 'lastarchon.master.roas_monthly_d7',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }
        ).create(),

        'lastarchon.master.roas_monthly_d30':
            new RoasMonthlyD30DataLoaderFactory({
                createdInstanceId: 'lastarchon.master.roas_monthly_d30',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.users_days_daily_d7':
            new UsersDaysDailyD7DataLoaderFactory({
                createdInstanceId: 'lastarchon.master.users_days_daily_d7',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.users_days_daily_d30':
            new UsersDaysDailyD30DataLoaderFactory({
                createdInstanceId: 'lastarchon.master.users_days_daily_d30',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),

        'lastarchon.master.users_days_monthly_d30':
            new UsersDaysMonthlyD30DataLoaderFactory({
                createdInstanceId: 'lastarchon.master.users_days_monthly_d30',
                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    FILTER DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        // 'lastarchon.master.filter.revenue_type': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'lastarchon.master.filter.revenue_type',
        //
        // gcpProjectId: () => {
        //     return getGcpProjectIdForEnv(this.dataEnv.current);
        // },
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'REVENUE_TYPE',
        //     datasourceSqlMap: BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap,
        // }).create(),
        'lastarchon.master.filter.project_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.master.filter.project_name',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'COMPANY_PROJECT_NAME',
            datasourceSqlMap: BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap,
        }).create(),
        'lastarchon.master.filter.first_geo_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'lastarchon.master.filter.first_geo_country_tier',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_GEO_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap,
        }).create(),
        'lastarchon.master.filter.first_geo_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.master.filter.first_geo_country',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_GEO_COUNTRY',
            datasourceSqlMap: BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap,
        }).create(),
        'lastarchon.master.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.master.filter.etl_payer',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'REVENUE_PAYER',
            datasourceSqlMap: BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap,
        }).create(),
    };
    /////////////////////////////////////////////////////////////////
    ////
    ////    DASHBOARD ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    SETTINGS ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    settingItemNetGross: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.NET_GROSS,
            viewtype: 'toggle',
            value: 'net',
            selectOptions: ['net', 'gross'],
        },
    };

    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            value: 'pln',
            selectOptions: ['pln', 'usd'],
        },
    };
    settingItemShowTotalRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_TOTAL_REVENUE,
            viewtype: 'checkbox',
            displayName: 'total',
            value: true,
        },
    };
    settingItemShowInappRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_INAPP_REVENUE,
            viewtype: 'checkbox',
            displayName: 'inapp',
        },
    };
    settingItemShowAdRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_AD_REVENUE,
            viewtype: 'checkbox',
            displayName: 'ad',
        },
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHARTS ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    avg_user_lifetime_d7: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.avg_user_lifetime_d7',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.avg_user_lifetime_d7',
            dashboardItem: {
                title: 'Average user lifetime D7',
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_user_lifetime_d7',
            metricName: 'Avg user lifetime D7',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `First open date: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v: number) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                v,
                                ' days'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.users_days_daily_d7']
                .emitter,
    };

    avg_user_lifetime_d30: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.avg_user_lifetime_d30',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.avg_user_lifetime_d30',
            dashboardItem: {
                title: 'Average user lifetime D30',
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_user_lifetime_d30',
            metricName: 'Avg user lifetime D30',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `First open date: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v: number) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                v,
                                ' days'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.users_days_daily_d30']
                .emitter,
    };
    avg_user_lifetime_d30_monthly: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.avg_user_lifetime_d30_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId:
                'lastarchon.master.chart.avg_user_lifetime_d30_monthly',
            dashboardItem: {
                title: 'Average user lifetime D30 Monthly',
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_user_lifetime_d30_monthly',
            metricName: 'Monthly avg user lifetime D30 ',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v: number) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                v,
                                ' days'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.users_days_monthly_d30']
                .emitter,
    };
    dau: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.dau',
            dashboardItem: {
                title: 'DAU',
                // subtitle: 'Unsupported filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'DAU',
            metricName: 'DAU',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_daily']
                .emitter,
    };
    stickiness_dau_mau: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.stickiness_dau_mau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.stickiness_dau_mau',
            dashboardItem: {
                title: 'Monthly average stickiness factor DAU/MAU ',
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'stickiness_dau_mau',
            metricName: 'Stickiness factor',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.activity_30days_monthly']
                .emitter,
    };
    new_users_daily: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.new_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.new_users',
            dashboardItem: {
                title: 'New users',
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_daily']
                .emitter,
    };
    monthly_avg_dau: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.monthly_avg_dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.monthly_avg_dau',
            dashboardItem: {
                title: 'Monthly average DAU',
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_DAU',
            metricName: 'Monthly avg DAU',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_monthly']
                .emitter,
    };
    mau: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.mau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.mau',
            dashboardItem: {
                title: 'MAU',
                // subtitle: 'Unsupported filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'MAU',
            metricName: 'MAU',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_monthly']
                .emitter,
    };
    new_users_monthly: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.new_users_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.new_users_monthly',
            dashboardItem: {
                title: 'Monthly new Users ',
                // subtitle: 'Unsupported filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'Monthly new users',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_monthly']
                .emitter,
    };
    daily_income: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.daily_income',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.daily_income',
            dashboardItem: { title: 'Daily income' },
            // yColumnLabelPrefix: 'amount',
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'revenue',
                metricName: 'Revenue',
            }),
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_daily']
                .emitter,
    };
    monthly_income: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.monthly_income',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.monthly_income',
            dashboardItem: { title: 'Monthly income' },
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'revenue',
                metricName: 'Revenue',
            }),
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_monthly']
                .emitter,
    };
    daily_ads_cost: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.daily_ads_cost',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'lastarchon.master.chart.daily_ads_cost',
            dashboardName: 'board',
            dashboardItem: {
                title: 'Daily ads campaign cost',
                // subtitle: 'Ignored filters: Revenue Type, Payer',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) =>
                `cost_${context.settings.currency}`,
            metricName: 'Ads cost',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.master.campaign_attribution_kpis_daily'
            ].emitter,
    };
    monthly_ads_cost: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.monthly_ads_cost',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'lastarchon.master.chart.monthly_ads_cost',
            dashboardName: 'board',
            dashboardItem: {
                title: 'Monthly ads campaign cost',
                // subtitle: 'Ignored filters: Revenue Type, Payer',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) =>
                `cost_${context.settings.currency}`,
            metricName: 'Monthly ads cost',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.master.campaign_attribution_kpis_monthly'
            ].emitter,
    };
    // TODO payers filter
    first_margin_daily: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.first_margin_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.first_margin_daily',
            dashboardItem: {
                title: 'Daily first margin ',
                // subtitle: 'Ignored filters: Payer',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) =>
                `first_margin_${context.settings.netGross}_${context.settings.currency}`,
            metricName: 'First margin',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.master.first_margin_daily_last_30days'
            ].emitter,
    };
    first_margin_monthly: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.first_margin_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.first_margin_monthly',
            dashboardItem: {
                title: 'Monthly first margin ',
                // subtitle: 'Ignored filters: Payer',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) =>
                `first_margin_${context.settings.currency}`,
            metricName: 'Monthly first margin',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.first_margin_monthly']
                .emitter,
    };
    arpdau: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.daily_arpdau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.daily_arpdau',
            dashboardItem: { title: 'Daily ARPDAU' },
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'ARPDAU',
                metricName: 'ARPDAU',
            }),
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_daily']
                .emitter,
    };
    monthly_avg_arpdau: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.monthly_avg_arpdau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.monthly_avg_arpdau',
            dashboardItem: { title: 'Monthly average ARPDAU' },
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'ARPDAU',
                metricName: 'Monthly avg ARPDAU',
            }),
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_monthly']
                .emitter,
    };
    running_arpu: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.running_arpu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.running_arpu',
            dashboardItem: { title: 'Running ARPU' },
            xColumnLabel: 'day_no',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'ARPU',
                metricName: 'Running ARPU',
            }),
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) => `Day no: ${label}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.cohort_revenue_daily_agg']
                .emitter,
    };
    monthly_arpu_d30: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.monthly_arpu_d30',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.monthly_arpu_d30',
            dashboardItem: { title: 'Monthly ARPU D30' },
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'ARPU_d30',
                metricName: 'ARPU d30',
            }),
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_monthly']
                .emitter,
    };
    conversion_rate: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.conversion_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.conversion_rate',
            dashboardItem: {
                title: 'Conversion rate',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'conversion_rate',
            metricName: 'Conversion rate',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipPercentValueFormatter(
                                v
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_daily']
                .emitter,
    };
    monthly_conversion_rate: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.monthly_conversion_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId:
                'lastarchon.master.chart.monthly_conversion_rate',
            dashboardItem: {
                title: 'Monthly Conversion rate',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'conversion_rate',
            metricName: 'Monthly conversion rate',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.player_kpis_monthly']
                .emitter,
    };
    daily_cpi: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.daily_cpi',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.daily_cpi',
            dashboardItem: {
                title: 'Daily CPI',
                // subtitle:
                //     'Total campaign costs over total (Organic & Non-Organic) installs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `CPI_${context.settings.currency}`;
            },
            metricName: 'CPI',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.master.campaign_attribution_kpis_daily'
            ].emitter,
    };
    monthly_cpi: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.monthly_cpi',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.monthly_cpi',
            dashboardItem: {
                title: 'Monthly CPI',
                // subtitle:
                //     'Total campaign costs over total (Organic & Non-Organic) installs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `CPI_${context.settings.currency}`;
            },
            metricName: 'Monthly CPI',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.master.campaign_attribution_kpis_monthly'
            ].emitter,
    };
    roas_d7_daily: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.roas_d7_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.roas_d7_daily',
            dashboardItem: {
                title: 'Average ROAS D7',
                // subtitle:
                //     'Total (Organic & Non-Organic) revenue over total campaign costs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `ROAS_d7_${netGross}${context.settings.currency}`;
            },
            metricName: 'ROAS D7',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `First open date: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.roas_daily_d7'].emitter,
    };
    roas_d7_monthly: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.roas_d7_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.roas_d7_monthly',
            dashboardItem: {
                title: 'Average ROAS D7 monthly',
                // subtitle:
                //     'Total (Organic & Non-Organic) revenue over total campaign costs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `ROAS_d7_${netGross}${context.settings.currency}`;
            },
            metricName: 'ROAS D7',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.roas_monthly_d7'].emitter,
    };
    roas_d30_daily: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.roas_d30_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.roas_d30_daily',
            dashboardItem: {
                title: 'Average ROAS D30',
                // subtitle:
                //     'Total (Organic & Non-Organic) revenue over total campaign costs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `ROAS_d30_${netGross}${context.settings.currency}`;
            },
            metricName: 'ROAS D30',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `First open date: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.roas_daily_d30'].emitter,
    };
    roas_d30_monthly: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.roas_d30_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.roas_d30_monthly',
            dashboardItem: {
                title: 'Average ROAS D30 monthly',
                // subtitle:
                //     'Total (Organic & Non-Organic) revenue over total campaign costs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `ROAS_d30_${netGross}${context.settings.currency}`;
            },
            metricName: 'ROAS D30',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.roas_monthly_d30'].emitter,
    };
    // ads engagement rate
    ads_engagement_rate: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.ads_engagement_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.ads_engagement_rate',
            dashboardItem: {
                title: 'Ads engagement rate',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'engagement_rate',
            metricName: 'Engagement rate',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            EChartsAxisHelper.axisLabelDateFormatter(label),
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipPercentValueFormatter(
                                v
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.ads_daily_last_30days']
                .emitter,
    };
    monthly_engagement_rate: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.monthly_engagement_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId:
                'lastarchon.master.chart.monthly_engagement_rate',
            dashboardItem: {
                title: 'Monthly avg. of daily engagement rate',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'engagement_rate',
            metricName: 'Monthly avg. of daily engagement rate',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.ads_monthly_last_16months']
                .emitter,
    };
    ads_impressions: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.ads_impressions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId: 'lastarchon.master.chart.ads_impressions',
            dashboardItem: {
                title: 'Ads impressions',
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions',
            metricName: 'Ads impressions',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.ads_daily_last_30days']
                .emitter,
    };
    ads_impressions_monthly: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.chart.ads_impressions_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'board',
            createdInstanceId:
                'lastarchon.master.chart.ads_impressions_monthly',
            dashboardItem: {
                title: 'Ads impressions monthly',
                // subtitle: 'Unsupported filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions',
            metricName: 'Ads impressions monthly',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.ads_monthly_last_16months']
                .emitter,
    };
    // impressions_per_deu
    impressions_per_deu: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.ads.chart.impressions_per_deu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'lastarchon.master.chart.impressions_per_deu',
            dashboardItem: { title: 'Impressions per DEU' },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions_per_deu',
            metricName: 'Impressions per DEU',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.ads_daily_last_30days']
                .emitter,
    }; // impressions_per_deu_monthly
    impressions_per_deu_monthly: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.master.ads.chart.impressions_per_deu_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'lastarchon.master.chart.impressions_per_deu_monthly',
            dashboardItem: {
                title: 'Monthly avg. of daily impressions per DEU',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions_per_deu',
            metricName: 'Monthly avg. of daily impressions per DEU',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.master.ads_monthly_last_16months']
                .emitter,
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    FILTER ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        COMPANY_PROJECT_NAME: {
            id: 'lastarchon.master.filter.project_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'lastarchon.master.filter.project_name',
                datasourceColumnId: 'COMPANY_PROJECT_NAME',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['lastarchon.master.filter.project_name']
                    .emitter,
        },
        FIRST_GEO_COUNTRY_TIER: {
            id: 'lastarchon.master.filter.first_geo_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'lastarchon.master.filter.first_geo_country_tier',
                datasourceColumnId: 'FIRST_GEO_COUNTRY_TIER',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.master.filter.first_geo_country_tier'
                ].emitter,
        },
        FIRST_GEO_COUNTRY: {
            id: 'lastarchon.master.filter.first_geo_country',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'lastarchon.master.filter.first_geo_country',
                datasourceColumnId: 'FIRST_GEO_COUNTRY',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.master.filter.first_geo_country'
                ].emitter,
        },
        // FIRST_ATTRIBUTION_UNIFIED_TRAFFIC: {
        //     id: 'lastarchon.master.filter.attribution_unified_traffic',
        //     dashboardItemFactory: DashboardItemFactories['filter.category']({
        //         createdInstanceId:
        //             'lastarchon.master.filter.attribution_unified_traffic',
        //         datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
        //         componentClass: 'CategoryFilterDashboardItem',
        //         excludeMode: false,
        //     }),
        //     dataLoaderEmitter:
        //         this.filterDataLoaders[
        //             'lastarchon.master.filter.attribution_unified_traffic'
        //         ].emitter,
        // },
        REVENUE_PAYER: {
            id: 'lastarchon.master.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'lastarchon.master.filter.etl_payer',
                datasourceColumnId: 'REVENUE_PAYER',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['lastarchon.master.filter.etl_payer']
                    .emitter,
        },
        // REVENUE_TYPE: {
        //     id: 'lastarchon.master.filter.revenue_type',
        //     dashboardItemFactory: DashboardItemFactories['filter.category']({
        //         createdInstanceId:
        //             'lastarchon.master.filter.revenue_type',
        //         datasourceColumnId: 'REVENUE_TYPE',
        //         componentClass: 'CategoryFilterDashboardItem',
        //         excludeMode: false,
        //     }),
        //     dataLoaderEmitter:
        //         this.filterDataLoaders[
        //             'lastarchon.master.filter.revenue_type'
        //         ].emitter,
        // },
    };

    override ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'CompanyboardComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    ngAfterViewInit() {
        console.log({
            msg: 'CompanyboardComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const projectNameIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'COMPANY_PROJECT_NAME',
                state: {
                    value: { options: [], filterType: 'include' },
                    // filterType: 'include',
                    isFilterApplied: false,
                    isSplitApplied: true,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIRST_GEO_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            projectNameIC,
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
        ];

        return initialContext;
    }
}
